'use client';

import { useEffect, useRef } from 'react';

import posthog from 'posthog-js';

// Written by CookieConsentProvider on the marketing surfaces; absent on
// authenticated app surfaces (no banner there) and for first-time visitors.
const CONSENT_STORAGE_KEY = 'cookie-consent';

function hasStoredAnalyticsConsent(): boolean {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return false;
    return Boolean((JSON.parse(raw) as { analytics?: boolean }).analytics);
  } catch {
    return false;
  }
}

/**
 * PostHogProvider initializes the posthog-js client in the browser.
 *
 * It reads NEXT_PUBLIC_POSTHOG_KEY and NEXT_PUBLIC_POSTHOG_HOST from
 * the environment. If either value is missing the provider renders its
 * children without initializing, which is the expected behavior during
 * local development.
 *
 * Cookie use is consent-aware: until the visitor grants the analytics
 * category (or an exempt jurisdiction grants it via the consent context),
 * persistence stays in memory — no analytics cookie or localStorage entry
 * is written, which is what the consent banner promises. On grant
 * (stored choice or a `cookie-consent-update` event) persistence upgrades
 * to localStorage+cookie; on revoke it drops back to memory.
 *
 * The provider must be rendered inside a "use client" boundary (e.g.
 * the root layout providers) so it only runs in the browser.
 */
export function PostHogProvider({ children }: React.PropsWithChildren) {
  const initialized = useRef(false);

  useEffect(() => {
    // Only initialize once, and only in the browser
    if (initialized.current) return;
    if (typeof window === 'undefined') return;

    const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;

    if (!apiKey || !host) return;

    posthog.init(apiKey, {
      // Use reverse proxy to bypass ad blockers (falls back to direct host)
      api_host: '/p',
      // Must be the region of the project behind NEXT_PUBLIC_POSTHOG_KEY
      ui_host: host,
      // Capture pageviews manually via the existing analytics provider
      capture_pageview: false,
      // Respect Do Not Track browser setting
      respect_dnt: true,
      // Cookieless until the analytics category is granted
      persistence: hasStoredAnalyticsConsent()
        ? 'localStorage+cookie'
        : 'memory',
      // Enable heatmaps toolbar
      opt_in_site_apps: true,
      // Session recording: mask all inputs for privacy
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-mask]',
      },
      enable_recording_console_log: false,
    });

    const onConsentUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ analytics?: boolean }>).detail;
      posthog.set_config({
        persistence: detail?.analytics ? 'localStorage+cookie' : 'memory',
      });
    };

    window.addEventListener('cookie-consent-update', onConsentUpdate);

    // The npm build of posthog-js does NOT attach itself to window (only the
    // script snippet does). Every marketing event call site uses the
    // null-safe `window.posthog?.capture` pattern, so without this handle
    // they all silently no-op: verified live 2026-07-05 — zero custom events
    // (receipts, spec, widget) had ever captured. Expose the initialized
    // instance for those consumers.
    (window as { posthog?: typeof posthog }).posthog = posthog;

    initialized.current = true;

    return () => {
      window.removeEventListener('cookie-consent-update', onConsentUpdate);
    };
  }, []);

  return <>{children}</>;
}
